import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useIndexCardsStore, useCardsStore } from '@/stores'
import { ReferencePicker } from '@/components/IndexCard'
import { formatLidForDisplay } from '@/utils/lid'
import '@/components/IndexCard/IndexCard.css'

const MAX_REFERENCES = 10

export default function IndexCardPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showPicker, setShowPicker] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editKeyword, setEditKeyword] = useState('')
  const [editDescription, setEditDescription] = useState('')

  const { getIndexCard, updateIndexCard, deleteIndexCard, addReference, removeReference, fetchIndexCards } =
    useIndexCardsStore()
  const { cards, fetchCards } = useCardsStore()

  const indexCard = id ? getIndexCard(id) : undefined

  useEffect(() => {
    fetchIndexCards()
    fetchCards()
  }, [fetchIndexCards, fetchCards])

  useEffect(() => {
    if (indexCard) {
      setEditKeyword(indexCard.keyword)
      setEditDescription(indexCard.description || '')
    }
  }, [indexCard])

  if (!id) {
    return <div>Invalid index card ID</div>
  }

  if (!indexCard) {
    return (
      <div className="index-card-page">
        <div className="index-empty-state">
          <h3>{t('card.notFound')}</h3>
          <p>{t('card.notFoundDesc')}</p>
          <Link to="/index" className="btn btn-primary">
            {t('common.back')}
          </Link>
        </div>
      </div>
    )
  }

  const handleSaveEdit = async () => {
    try {
      await updateIndexCard(id, {
        keyword: editKeyword,
        description: editDescription || undefined,
      })
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to update index card:', error)
    }
  }

  const handleDelete = async () => {
    if (window.confirm(t('index.delete.confirm'))) {
      try {
        await deleteIndexCard(id)
        navigate('/index')
      } catch (error) {
        console.error('Failed to delete index card:', error)
      }
    }
  }

  const handleAddReference = async (lid: string) => {
    try {
      await addReference(id, lid)
      setShowPicker(false)
    } catch (error) {
      console.error('Failed to add reference:', error)
    }
  }

  const handleRemoveReference = async (lid: string) => {
    try {
      await removeReference(id, lid)
    } catch (error) {
      console.error('Failed to remove reference:', error)
    }
  }

  const getCardTitle = (lid: string): string => {
    const card = cards.get(lid)
    return card?.title || lid
  }

  const canAddMoreReferences = indexCard.references.length < MAX_REFERENCES

  return (
    <div className="index-card-page">
      <header className="index-card-header">
        <Link to="/index" className="btn btn-ghost" style={{ marginBottom: '1rem' }}>
          &larr; {t('common.back')}
        </Link>

        {isEditing ? (
          <div className="index-card-form">
            <div className="form-group">
              <label>{t('index.form.keywordLabel')}</label>
              <input
                type="text"
                value={editKeyword}
                onChange={(e) => setEditKeyword(e.target.value)}
                placeholder={t('index.form.keywordPlaceholder')}
              />
            </div>
            <div className="form-group">
              <label>{t('index.form.descriptionLabel')}</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t('index.form.descriptionPlaceholder')}
              />
            </div>
            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setIsEditing(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSaveEdit}>
                {t('common.save')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h1>
              <span className="topic-id-badge topic-id-badge--index">17/</span>
              {indexCard.keyword}
            </h1>
            <div className="index-card-actions">
              <button className="btn btn-secondary" onClick={() => setIsEditing(true)}>
                {t('common.edit')}
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                {t('common.delete')}
              </button>
            </div>
          </>
        )}
      </header>

      {!isEditing && indexCard.description && (
        <div className="index-card-description-section">
          <h3>{t('index.form.descriptionLabel')}</h3>
          <p>{indexCard.description}</p>
        </div>
      )}

      <section className="index-references-section">
        <div className="index-references-header">
          <h3>{t('index.form.referencesLabel')}</h3>
          <span className="references-count">
            {indexCard.references.length}/{MAX_REFERENCES}
          </span>
        </div>

        {indexCard.references.length === 0 ? (
          <p className="index-references-empty">{t('index.form.referencesHint')}</p>
        ) : (
          <div className="index-references-list">
            {indexCard.references.map((lid) => (
              <div key={lid} className="index-reference-item">
                <Link to={`/card/${lid}`} className="reference-lid-badge">
                  {formatLidForDisplay(lid)}
                </Link>
                <Link to={`/card/${lid}`} className="reference-title">
                  {getCardTitle(lid)}
                </Link>
                <button
                  className="reference-remove-btn"
                  onClick={() => handleRemoveReference(lid)}
                  title={t('index.removeReference')}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          className="add-reference-btn"
          onClick={() => setShowPicker(true)}
          disabled={!canAddMoreReferences}
        >
          + {canAddMoreReferences
              ? t('index.addReference')
              : t('index.referenceLimitReached', { max: MAX_REFERENCES })}
        </button>
      </section>

      {showPicker && (
        <ReferencePicker
          existingReferences={indexCard.references}
          onSelect={handleAddReference}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  )
}
